import { format } from "date-fns";

import { es } from "date-fns/locale";

import {
  Archive,
  CheckCircle,
  Download,
  FileEdit,
  Package,
  Trash2,
} from "lucide-react";

import { useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { Badge } from "@/components/ui/badge";

import { Button } from "@/components/ui/button";

import { Card, CardContent } from "@/components/ui/card";

import { Input } from "@/components/ui/input";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import EmptyState from "../../../shared/components/EmptyState";

import LoadingOverlay from "../../../shared/components/LoadingOverlay";

import PageHeader from "../../../shared/components/PageHeader";

import {
  useArchiveRelease,
  useDeleteRelease,
  useInstallerReleases,
  useInstallerStats,
  usePublishRelease,
} from "../hooks/useInstaller";

import CreateReleaseModal from "./CreateReleaseModal";

interface StatsCardProps {
  title: string;

  value: number | string;

  icon: React.ReactNode;

  iconColor?: string;
}

const StatsCard = ({
  title,
  value,
  icon,
  iconColor = "text-purple-400",
}: StatsCardProps) => (
  <Card className="border-neutral-800 bg-neutral-900/50">
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-xs uppercase tracking-wide text-neutral-500">
            {title}
          </p>
          <p className="mt-1 text-2xl font-bold text-white">
            {typeof value === "number" ? value.toLocaleString("es-AR") : value}
          </p>
        </div>
        <div className={`rounded-lg bg-neutral-800/50 p-2.5 ${iconColor}`}>
          {icon}
        </div>
      </div>
    </CardContent>
  </Card>
);

const StatusBadge = ({
  status,
}: {
  status: "draft" | "published" | "archived";
}) => {
  const config = {
    draft: { className: "bg-yellow-500/20 text-yellow-400", label: "Borrador" },

    published: {
      className: "bg-green-500/20 text-green-400",
      label: "Publicado",
    },

    archived: {
      className: "bg-neutral-700 text-neutral-400",
      label: "Archivado",
    },
  };

  const { className, label } = config[status];

  return <Badge className={className}>{label}</Badge>;
};

const PlatformBadge = ({ platform }: { platform: string }) => {
  const labels: Record<string, string> = {
    windows: "Windows",

    macos: "macOS",

    linux: "Linux",
  };

  return (
    <Badge variant="outline" className="border-neutral-600 text-neutral-300">
      {labels[platform] || platform}
    </Badge>
  );
};

const EnvironmentBadge = ({
  environment,
}: {
  environment: "dev" | "staging" | "production";
}) => {
  const config = {
    dev: { className: "bg-neutral-700 text-neutral-300", label: "Dev" },

    staging: { className: "bg-blue-500/20 text-blue-400", label: "Staging" },

    production: {
      className: "bg-amber-500/20 text-amber-400 font-semibold",
      label: "Production",
    },
  };

  const { className, label } = config[environment];

  return <Badge className={className}>{label}</Badge>;
};

type ConfirmAction = {
  type: "publish" | "archive" | "delete";

  id: string;

  version: string;
} | null;

const InstallerPage = () => {
  const [statusFilter, setStatusFilter] = useState<
    "all" | "draft" | "published" | "archived"
  >("all");

  const [platformFilter, setPlatformFilter] = useState<
    "all" | "windows" | "macos" | "linux"
  >("all");

  const [environmentFilter, setEnvironmentFilter] = useState<
    "all" | "dev" | "staging" | "production"
  >("all");

  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  const { data: releasesData, isLoading: isLoadingReleases } =
    useInstallerReleases({
      page: currentPage,

      limit: 20,

      status: statusFilter,

      platform: platformFilter,

      environment: environmentFilter,

      search: searchTerm,
    });

  const { data: stats } = useInstallerStats();

  const publishRelease = usePublishRelease();

  const archiveRelease = useArchiveRelease();

  const deleteRelease = useDeleteRelease();

  const handleSearch = (value: string) => {
    setSearchTerm(value);

    setCurrentPage(1);
  };

  const handleStatusFilterChange = (filter: typeof statusFilter) => {
    setStatusFilter(filter);

    setCurrentPage(1);
  };

  const handlePlatformFilterChange = (filter: typeof platformFilter) => {
    setPlatformFilter(filter);

    setCurrentPage(1);
  };

  const handleEnvironmentFilterChange = (filter: typeof environmentFilter) => {
    setEnvironmentFilter(filter);

    setCurrentPage(1);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd MMM yyyy HH:mm", { locale: es });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;

    const sizes = ["Bytes", "KB", "MB", "GB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    try {
      switch (confirmAction.type) {
        case "publish":
          await publishRelease.mutateAsync(confirmAction.id);

          break;

        case "archive":
          await archiveRelease.mutateAsync(confirmAction.id);

          break;

        case "delete":
          await deleteRelease.mutateAsync(confirmAction.id);

          break;
      }
    } finally {
      setConfirmAction(null);
    }
  };

  const releases = releasesData?.data || [];

  const totalPages = releasesData?.totalPages || 1;

  const hasReleases = releases.length > 0;

  const statusLabels = {
    all: "Todos",

    published: "Publicado",

    draft: "Borrador",

    archived: "Archivado",
  };

  const platformLabels = {
    all: "Todas",
    windows: "Windows",
    macos: "macOS",
    linux: "Linux",
  };

  const getConfirmDialogContent = () => {
    if (!confirmAction) return { title: "", description: "", actionLabel: "" };

    switch (confirmAction.type) {
      case "publish":
        return {
          title: "Publicar versión",

          description: `¿Estás seguro de que deseas publicar la versión ${confirmAction.version}? Esta versión estará disponible para todos los usuarios.`,

          actionLabel: "Publicar",
        };

      case "archive":
        return {
          title: "Archivar versión",

          description: `¿Estás seguro de que deseas archivar la versión ${confirmAction.version}? Los usuarios ya no podrán descargar esta versión.`,

          actionLabel: "Archivar",
        };

      case "delete":
        return {
          title: "Eliminar versión",

          description: `¿Estás seguro de que deseas eliminar la versión ${confirmAction.version}? Esta acción borra el archivo y no se puede deshacer.`,

          actionLabel: "Eliminar",
        };
    }
  };

  const dialogContent = getConfirmDialogContent();

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Instalador"
        subtitle="Gestión de versiones del instalador"
        actions={
          <Button
            onClick={() => setIsModalOpen(true)}
            className="bg-purple-600 text-white hover:bg-purple-700"
          >
            Nueva Versión
          </Button>
        }
      />

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <StatsCard
            title="Total versiones"
            value={stats.totalReleases}
            icon={<Package className="h-5 w-5" />}
            iconColor="text-purple-400"
          />
          <StatsCard
            title="Publicadas"
            value={stats.publishedReleases}
            icon={<CheckCircle className="h-5 w-5" />}
            iconColor="text-green-400"
          />
          <StatsCard
            title="Borradores"
            value={stats.draftReleases}
            icon={<FileEdit className="h-5 w-5" />}
            iconColor="text-yellow-400"
          />
          <StatsCard
            title="Descargas totales"
            value={stats.totalDownloads}
            icon={<Download className="h-5 w-5" />}
            iconColor="text-blue-400"
          />
          <StatsCard
            title="Última versión"
            value={stats.latestVersion}
            icon={<Archive className="h-5 w-5" />}
            iconColor="text-amber-400"
          />
        </div>
      )}

      <Card className="border-neutral-800 bg-neutral-900/50">
        <CardContent className="space-y-4 p-4 gap-4">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-20">
            <div className="flex flex-wrap gap-2">
              {(["all", "published", "draft", "archived"] as const).map(
                (filter) => (
                  <Button
                    key={filter}
                    variant={statusFilter === filter ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleStatusFilterChange(filter)}
                    className={
                      statusFilter === filter
                        ? "bg-purple-600 text-white hover:bg-purple-700"
                        : "border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                    }
                  >
                    {statusLabels[filter]}
                  </Button>
                ),
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {(["all", "windows", "macos", "linux"] as const).map((filter) => (
                <Button
                  key={filter}
                  variant="outline"
                  size="sm"
                  onClick={() => handlePlatformFilterChange(filter)}
                  className={
                    platformFilter === filter
                      ? "border-purple-500 text-purple-400"
                      : "border-neutral-700 text-neutral-400 hover:border-neutral-600"
                  }
                >
                  {platformLabels[filter]}
                </Button>
              ))}
            </div>

            {/* Environment Filter */}
            <div className="flex flex-wrap gap-1">
              {(["all", "dev", "staging", "production"] as const).map(
                (filter) => (
                  <Button
                    key={filter}
                    variant="outline"
                    size="sm"
                    onClick={() => handleEnvironmentFilterChange(filter)}
                    className={
                      environmentFilter === filter
                        ? "border-blue-500 text-blue-400"
                        : "border-neutral-700 text-neutral-400 hover:border-neutral-600"
                    }
                  >
                    {filter === "all"
                      ? "Todos"
                      : filter === "dev"
                        ? "Dev"
                        : filter === "staging"
                          ? "Staging"
                          : "Production"}
                  </Button>
                ),
              )}
            </div>

            <div className="flex-1">
              <Input
                type="text"
                placeholder="Buscar por versión..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
              />
            </div>
          </div>

          {isLoadingReleases ? (
            <div className="py-12">
              <LoadingOverlay />
            </div>
          ) : !hasReleases ? (
            <EmptyState
              title="No hay versiones"
              description={
                searchTerm
                  ? "No se encontraron versiones con ese criterio de búsqueda."
                  : "Aún no hay versiones publicadas del instalador."
              }
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800 hover:bg-transparent">
                    <TableHead className="text-neutral-400">Versión</TableHead>
                    <TableHead className="text-neutral-400">Estado</TableHead>
                    <TableHead className="text-neutral-400">
                      Plataforma
                    </TableHead>
                    <TableHead className="text-neutral-400">Entorno</TableHead>
                    <TableHead className="text-neutral-400">Tamaño</TableHead>
                    <TableHead className="text-neutral-400">
                      Descargas
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      Publicado
                    </TableHead>
                    <TableHead className="text-neutral-400">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {releases.map((release) => (
                    <TableRow
                      key={release.id}
                      className="border-neutral-800 hover:bg-neutral-800/50"
                    >
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-semibold text-neutral-200">
                            {release.version}
                          </span>
                          <span className="text-xs text-neutral-500">
                            {release.fileName}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={release.status} />
                      </TableCell>
                      <TableCell>
                        <PlatformBadge platform={release.platform} />
                      </TableCell>
                      <TableCell>
                        <EnvironmentBadge environment={release.environment} />
                      </TableCell>
                      <TableCell className="text-neutral-400">
                        {formatFileSize(release.fileSize)}
                      </TableCell>
                      <TableCell className="text-neutral-400">
                        {release.downloads.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-neutral-400">
                        {release.publishedAt
                          ? formatDate(release.publishedAt)
                          : "-"}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {release.status === "draft" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setConfirmAction({
                                  type: "publish",

                                  id: release.id,

                                  version: release.version,
                                })
                              }
                              className="bg-green-500/10 text-green-400 hover:bg-green-500/20"
                            >
                              Publicar
                            </Button>
                          )}
                          {release.status === "published" && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                setConfirmAction({
                                  type: "archive",

                                  id: release.id,

                                  version: release.version,
                                })
                              }
                              className="bg-neutral-700 text-neutral-300 hover:bg-neutral-600"
                            >
                              Archivar
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            asChild
                            className="bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"
                          >
                            <a href={release.downloadUrl} download>
                              Descargar
                            </a>
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setConfirmAction({
                                type: "delete",

                                id: release.id,

                                version: release.version,
                              })
                            }
                            className="bg-red-500/10 text-red-400 hover:bg-red-500/20"
                            aria-label={`Eliminar versión ${release.version}`}
                            title="Eliminar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-neutral-800 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                    disabled={currentPage === 1}
                    className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  >
                    Anterior
                  </Button>
                  <span className="text-sm text-neutral-400">
                    Página {currentPage} de {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  >
                    Siguiente
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Modal */}
      <AlertDialog
        open={confirmAction !== null}
        onOpenChange={() => setConfirmAction(null)}
      >
        <AlertDialogContent className="border-neutral-800 bg-neutral-900">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-neutral-100">
              {dialogContent.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400">
              {dialogContent.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-neutral-700 bg-transparent text-neutral-300 hover:bg-neutral-800 hover:text-neutral-100">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmAction}
              className={
                confirmAction?.type === "delete"
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-purple-600 text-white hover:bg-purple-700"
              }
            >
              {dialogContent.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isModalOpen && (
        <CreateReleaseModal onClose={() => setIsModalOpen(false)} />
      )}
    </div>
  );
};

export default InstallerPage;
